import React, { useEffect, useRef, useState } from 'react';

import { faSparkle } from '@fortawesome/pro-regular-svg-icons';

import { add, isValidDate, parseDate } from 'helpers/date_utils';

import ManagedFormGroup from 'components/form/managed_input/form_group';
import { Button } from 'components/ui/button';
import { PdfDropZone } from './pdf_drop_zone';
import { PreExtractionInputs } from './pre_extraction_inputs';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { isFileReference } from './types';
import { updateLeaseTerm } from 'components/lease_structure/form/helpers/lease_term';
import { useBuildDealWithAi } from './use_build_deal_with_ai';

import type { Deal } from 'bundles/apex/deals/types';
import type { DealFile, Options, Paths } from './types';

interface Props {
  autoSubmit: boolean;
  deal: Deal | null;
  file: DealFile | null;
  initialDealType?: string;
  initialStage?: string;
  initialText?: string;
  options: Options;
  paths: Paths;
  onFileChange: (file: DealFile | null) => void;
  onFinish: (deal: Deal | null) => void;
  onStart: () => void;
}

export function ExtractionInputs({
  autoSubmit,
  deal,
  file,
  initialDealType,
  initialStage,
  initialText,
  options,
  paths,
  onFileChange,
  onFinish,
  onStart,
}: Props) {
  const [dealType, setDealType] = useState(initialDealType || '');
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(initialStage || '');
  const [text, setText] = useState(initialText || '');

  const autoSubmitted = useRef(false);

  const buildDeal = useBuildDealWithAi(paths.buildDealWithAi);

  async function extract() {
    const formData = new FormData();
    formData.append('text', text);

    if (dealType) {
      formData.append('deal_type', dealType);
    }

    if (stage) {
      formData.append('stage', stage);
    }

    if (file && isFileReference(file)) {
      formData.append('attachment_gid', file.gid);
    } else if (file) {
      formData.append('file', file);
    }

    setLoading(true);
    onStart();

    const builtDeal = await buildDeal(formData);
    if (builtDeal) {
      triggerLeaseTermCalcs(builtDeal);
    } else {
      showSnackbar({ text: 'Extraction failed', variant: 'danger' });
    }

    onFinish(builtDeal);
    setLoading(false);
  }

  // When the assistant prefills and opens the modal, run extraction once without waiting for a
  // click. The caller gates autoSubmit until any referenced file has settled into `file`.
  useEffect(() => {
    if (autoSubmit && !autoSubmitted.current && (file || text.trim())) {
      autoSubmitted.current = true;
      void extract();
    }
  }, [autoSubmit, file, text]);

  return (
    <div className="d-flex flex-column gap-3">
      <PdfDropZone file={file} onFileChange={onFileChange} />

      <ManagedFormGroup
        autoHeight
        label="Details"
        rows={5}
        sublabel="Explain a deal or add details from uploaded documents."
        type="textarea"
        value={text}
        onValueChange={setText}
      />

      {!deal && (
        <PreExtractionInputs
          dealType={dealType}
          options={options}
          stage={stage}
          onDealTypeChange={setDealType}
          onStageChange={setStage}
        />
      )}

      <Button
        className="w-100"
        disabled={(!file && !text.trim()) || loading}
        icon={faSparkle}
        label="Create Deal with AI"
        onClick={extract}
      />
    </div>
  );
}

// The LLM may have only extracted partial start date, end date, and duration information
// Run each term through the change actions to force recalculation of missing values
function triggerLeaseTermCalcs(deal: Deal) {
  if (!deal.leaseTerms) {
    return;
  }

  let startDate = deal.leaseStartDate;

  deal.leaseTerms = deal.leaseTerms.map((leaseTerm) => {
    leaseTerm = { ...leaseTerm };
    if (!leaseTerm.startDate && startDate) {
      leaseTerm.startDate = startDate;
    }

    let key: string | null = null;
    if (leaseTerm.months) {
      key = 'months';
    } else if (leaseTerm.endDate) {
      key = 'endDate';
    } else if (leaseTerm.startDate) {
      key = 'startDate';
    }

    if (key) {
      leaseTerm = updateLeaseTerm(deal, leaseTerm, key);
    }

    if (leaseTerm.endDate) {
      const nextStartDate = add(parseDate(leaseTerm.endDate), 1, 'day');
      startDate = isValidDate(nextStartDate) ? nextStartDate.toISOString() : null;
    }
    return leaseTerm;
  });
}
